/**
 * @fileoverview Prompt template for world-organization-details-generator-v3.
 *
 * Supports multimodal input with optional reference images.
 */

export const prompt = `
You are an expert world-builder specializing in creating rich, detailed, and complex organizations for fiction.

## TASK INSTRUCTIONS:
Your task is to generate complete details for the specified world organization, following the selected creative direction.

1. Review the organization's basic information and the selected creative direction.
{{#if hasImages}}
2. Use the provided reference images to inform visual details, atmosphere, and styling.
3. Generate detailed content for each attribute field listed below.
{{else}}
2. Generate detailed content for each attribute field listed below.
{{/if}}
{{#if hasImages}}4{{else}}3{{/if}}. Ensure all details:
   - Are consistent with the selected creative direction
   - Fit naturally within the story's lore and setting
   - Have narrative potential (create hooks for plot or character development)
   - Are specific and evocative, not generic
   - Include vivid details that bring the organization to life
{{#if hasImages}}
   - Incorporate visual elements from the reference images where appropriate
{{/if}}

{{#if hasImages}}
## IMAGE VALIDATION

Before using the reference images, perform a quick validation:
- The images should contain visual content (photos, artwork, illustrations, etc.)
- Images that are primarily text (documents, screenshots of text, etc.) are NOT valid visual references

If the images are NOT valid visual references:
- Set \`isValidImage\` to \`false\`
- Provide a brief \`validationError\` message explaining why
- Do NOT provide \`attributes\`

If the images ARE valid:
- Set \`isValidImage\` to \`true\`
- Use them as visual inspiration for the organization's details
- Provide the \`attributes\` object with generated content

## REFERENCE IMAGES

The provided images should be used as visual references for this organization. Extract relevant details such as:
- Symbols, banners, uniforms, or insignia
- Architecture and headquarters style
- Atmosphere, mood, and cultural tone
- Color palette and visual themes
- Any distinctive visual characteristics
{{/if}}

<<<userMessage>>>

## ORGANIZATION TO DEVELOP

<<<activeWorldOrganization>>>

## SELECTED CREATIVE DIRECTION

{{{json selectedIdea}}}

## STORY CONTEXT

<<<loreContext>>>

<<<storyContext>>>

<<<characters>>>

## OUTPUT INSTRUCTIONS:

{{#if hasImages}}
Your output MUST include:
- \`isValidImage\`: boolean - true if reference images are valid visual content, false if they're just text
- \`validationError\`: string (only if isValidImage is false) - brief explanation of why images are invalid
- \`attributes\`: object (only if isValidImage is true) - the generated organization details
{{else}}
Your output MUST include:
- \`isValidImage\`: true (no images to validate)
- \`attributes\`: object - the generated organization details
{{/if}}

For the \`attributes\` object, generate content for each of the following fields. All fields are optional, but provide as much detail as makes sense for this organization. Focus on fields most relevant to the selected creative direction.

{{#each organizationFields}}
- **{{this.label}}** (key: {{this.key}}): {{{this.description}}}
{{/each}}

Provide your output in the required JSON format.
`;
